import React from 'react';

/**
 * The small slice of markdown the assistant actually writes, rendered as React
 * elements — headings, bullet and numbered lists, bold, italic, inline code.
 *
 * Chat bubbles rendered `{text}` inside a `whitespace-pre-wrap` paragraph, so
 * every `**bold**` and `- bullet` the model emitted reached the user as literal
 * asterisks. The model has always written markdown; nothing was reading it.
 *
 * ⭐⭐ NO `dangerouslySetInnerHTML`, ANYWHERE. This builds React nodes from text,
 * so model output — which passes through user-controlled context and tool
 * results — has **no path to HTML injection at all**. That property is the
 * reason this is hand-rolled rather than reaching for a markdown library plus a
 * sanitiser: the safe thing is also the smaller thing here.
 *
 * ⚠️ DELIBERATELY PARTIAL. Tables, blockquotes, links, images and fenced code
 * are not handled — the assistant does not emit them (its suggestion fences are
 * stripped upstream by `stripSuggestionMarkup`). Anything unrecognised falls
 * through as plain text, which is exactly what shipped before, so an unhandled
 * construct can only ever look like today rather than break.
 */

/**
 * ⚠️⚠️ `_underscore_` ITALIC IS DELIBERATELY NOT SUPPORTED. The assistant writes
 * about `first_call`, `cache_read_input_tokens`, `chat_message` — snake_case
 * identifiers are ordinary content here, and treating `_` as an emphasis marker
 * would silently eat the middle of them. `*single asterisk*` carries italic.
 * ⚠️ Excluded in TWO places — this pattern and `renderInline`'s branches. Adding
 * `_` to only one of them changes nothing, which is a trap for anyone
 * "restoring" underscore support and concluding it already works.
 *
 * ⚠️⚠️ `[^*]+` IS LAZY ON PURPOSE. `.*` here would make `**a** and **b**` a
 * single match rendering as one bold blob reading "a** and **b".
 */
const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g;

/** Bold / italic / code inside one line of text. */
export function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  // ⚠️ `split` with a capturing group keeps the delimiters, so unmatched markers
  // (a lone `**`) simply arrive as ordinary text instead of swallowing the rest
  // of the message — the failure mode that makes naive replace() dangerous.
  const parts = text.split(INLINE);
  parts.forEach((part, i) => {
    if (!part) return;
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      out.push(<strong key={key} className="font-semibold">{part.slice(2, -2)}</strong>);
    } else if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      out.push(
        <code key={key} className="px-1 py-0.5 rounded bg-background/60 text-[0.9em] font-mono">
          {part.slice(1, -1)}
        </code>,
      );
    } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      out.push(<em key={key}>{part.slice(1, -1)}</em>);
    } else {
      out.push(<React.Fragment key={key}>{part}</React.Fragment>);
    }
  });
  return out;
}

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'h'; level: number; text: string };

const BULLET = /^\s*[-*+]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^\s*(#{1,6})\s+(.*)$/;

/** Group lines into blocks; consecutive list items become one list. */
export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  for (const raw of text.split('\n')) {
    const heading = HEADING.exec(raw);
    const bullet = BULLET.exec(raw);
    const numbered = NUMBERED.exec(raw);
    const last = blocks[blocks.length - 1];

    if (heading) {
      blocks.push({ kind: 'h', level: heading[1].length, text: heading[2] });
    } else if (bullet) {
      if (last?.kind === 'ul') last.items.push(bullet[1]);
      else blocks.push({ kind: 'ul', items: [bullet[1]] });
    } else if (numbered) {
      if (last?.kind === 'ol') last.items.push(numbered[1]);
      else blocks.push({ kind: 'ol', items: [numbered[1]] });
    } else if (raw.trim() === '') {
      // Keeps the blank line inside a paragraph run so the gap survives
      // `join('\n')` + `whitespace-pre-wrap`.
      //
      // ⚠️ This does NOT close lists, and an earlier comment here claiming it
      // did was wrong — mutation testing showed disabling this branch changes
      // nothing. A list is already closed by any line that is not a list item,
      // via the fallthrough below. Do not "fix" a list-merging bug here.
      if (last?.kind === 'p') last.lines.push('');
      else blocks.push({ kind: 'p', lines: [] });
    } else if (last?.kind === 'p') {
      last.lines.push(raw);
    } else {
      blocks.push({ kind: 'p', lines: [raw] });
    }
  }
  return blocks;
}

/** Render the assistant's markdown subset. */
export function ChatMarkdown({ text }: { text: string }) {
  const blocks = parseBlocks(text);
  return (
    <div className="text-sm break-words space-y-2">
      {blocks.map((b, i) => {
        if (b.kind === 'h') {
          return (
            <p key={i} className={`font-semibold ${b.level <= 2 ? 'text-[0.95rem]' : ''}`}>
              {renderInline(b.text, `h${i}`)}
            </p>
          );
        }
        if (b.kind === 'ul' || b.kind === 'ol') {
          const List = b.kind === 'ul' ? 'ul' : 'ol';
          return (
            <List
              key={i}
              className={`pl-5 space-y-1 ${b.kind === 'ul' ? 'list-disc' : 'list-decimal'}`}
            >
              {b.items.map((item, j) => (
                <li key={j} className="pl-0.5">{renderInline(item, `${i}-${j}`)}</li>
              ))}
            </List>
          );
        }
        const body = b.lines.join('\n').trim();
        if (!body) return null; // blank-line separators leave nothing to draw
        return (
          <p key={i} className="whitespace-pre-wrap">{renderInline(body, `p${i}`)}</p>
        );
      })}
    </div>
  );
}
